import { StrictMode, useEffect, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './styles.css';
import { App } from './app';
import { BrandProvider } from './brand/BrandProvider';
import { USE_MOCKS } from './api/client';
import { bootstrapSession } from './api/auth';
import { ErrorBoundary } from './components/ErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Gate the app on a one-time silent session restore (real-API mode). The access
 * token lives only in memory, so a hard reload drops it — but the httpOnly
 * refresh cookie survives, and bootstrapSession() trades it for a fresh token
 * before any page fires a request, avoiding the "logged-in but 401/empty" flash.
 * Mock mode renders immediately (no bootstrap).
 */
function SessionGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(USE_MOCKS);
  useEffect(() => {
    if (USE_MOCKS) return;
    let alive = true;
    void bootstrapSession().finally(() => {
      if (alive) setReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-white dark:bg-zinc-950 text-sm text-zinc-500 dark:text-zinc-400">
        Restoring your session…
      </div>
    );
  }
  return <>{children}</>;
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SessionGate>
          <BrandProvider>
            <App />
          </BrandProvider>
        </SessionGate>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
